<template>
  <div class="student-assignment-container">
    <div class="header-section" v-if="showUserList">
      <a role="button" @click="changeView()" class="back-button">
        <i class="fas fa-arrow-left"></i>
      </a>
      <label class="main-label">Back to list</label>
    </div>

    <div class="searchbar" v-if="showUserList">
      <input
          type="search"
          autocomplete="off"
          placeholder="Search by student name, email, or phone number"
          class="search-input"
          v-model="searchText"
          @keyup.enter="searchData"
      />
      <a role="button" @click="searchData" class="search-icon">
        <i class="fas fa-search"></i>
      </a>
    </div>

    <div v-if="showUserList" class="students-list" @scroll="handleScroll">
      <div v-for="student in filteredStudentList" :key="student.id" class="student-item">
        <input
            type="checkbox"
            :id="`student${student.id}`"
            @change="toggleStudentSelection(student)"
            :checked="isStudentSelected(student.id)"
        />
        <label :for="`student${student.id}`" class="student-label">
          <div class="student-name mb-0">{{ student.id }} - {{ student.firstName }} {{ student.lastName }}</div>
          <div class="student-details text-muted">{{ student.email }} ({{ student.username }})</div>
          <div class="student-details text-muted">{{ student.phone || 'No phone' }}</div>
        </label>
      </div>

      <div v-if="fetchingNextRows" class="loading-section">
        <div class="spinner"></div>
        Loading more students...
      </div>

      <div v-if="!fetchingNextRows && filteredStudentList.length === 0 && !isSearching" class="no-content">
        No students found
      </div>
    </div>

    <div class="header-actions" v-if="showUserList">
      <button class="assign-btn"
              @click="assignSelectedStudents()"
              :disabled="selectedStudents.length === 0 || assigning"
      >
          <span v-if="!assigning">
            <i class="far fa-user-plus"></i> Assign selected accounts ({{ selectedStudents.length }})
          </span>
        <span v-else> Assigning... </span>
      </button>
    </div>

    <div v-else-if="assignedStudents.length > 0" class="table-container">
      <table class="table">
        <thead>
        <tr>
          <th style="width: 5%">#</th>
          <th style="width: 7%">ID</th>
          <th style="width: 25%">Name</th>
          <th style="width: 18%">Username</th>
          <th style="width: 20%">Email</th>
          <th style="width: 15%">Phone</th>
          <th style="width: 10%">Actions</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(student, index) in assignedStudents" :key="student.id">
          <td class="text-center">{{ index + 1 }}</td>
          <td>{{ student.id }}</td>
          <td class="truncate">{{ shortenedFullName(student.firstName, student.lastName) }}</td>
          <td class="truncate">{{ student.username }}</td>
          <td class="truncate">{{ student.email }}</td>
          <td class="truncate">{{ student.phone || "-" }}</td>
          <td>
            <button
                @click="showUnassignConfirmPopup($event, student)"
                class="unassign-btn"
                :disabled="unassigningIds.length > 0 && unassigningIds.includes(student.id)"
            >
                  <span v-if="!unassigningIds.includes(student.id)">
                    <i class="fas fa-times-circle"></i> Remove
                  </span>
              <span v-else>Removing...</span>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="loading" class="loading-section">
      <div class="spinner"></div>
      Loading students...
    </div>

    <div v-else class="no-content">
      <div class="empty-state">
        <i class="fas fa-users fa-3x"></i>
        <h5>No students linked with this Parent account</h5>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { userService } from "../../../services";
import { UserStatus, UserType } from "../../../constants/user";

export default {
  name: "ManageChildren",
  props: {
    user: { type: Object, required: true },
    mode: { type: Number, default: 0 }
  },

  data() {
    return {
      assignedStudents: [],
      allAvailableStudents: [],
      selectedStudents: [],
      loading: false,
      showUserList: false,
      searchText: "",
      searchString: undefined,
      nextPageToken: undefined,
      pageSize: 100,
      isNextRecordAvailable: true,
      fetchingNextRows: false,
      isSearching: false,
      UserStatus,
      UserType,
      assigning: false,
      unassigningIds: []
    };
  },

  computed: {
    filteredStudentList() {
      const assignedIds = this.assignedStudents.map(student => student.id);
      const studentsNotAlreadyAssigned = this.allAvailableStudents.filter(student =>
          !assignedIds.includes(student.id)
      );

      const text = this.searchText.toLowerCase();
      return studentsNotAlreadyAssigned.filter(student => {
        return (
            (student.id && student.id.toLowerCase().includes(text)) ||
            (student.firstName && student.firstName.toLowerCase().includes(text)) ||
            (student.lastName && student.lastName.toLowerCase().includes(text)) ||
            (student.email && student.email.toLowerCase().includes(text)) ||
            (student.username && student.username.toLowerCase().includes(text)) ||
            (student.phone && student.phone.toLowerCase().includes(text))
        );
      });
    },
  },

  watch: {
    mode(newMode) {
      if (newMode === 6) this.changeView();
    }
  },

  methods: {
    ...mapActions("user", [
      "getParentChildren",
      "removeChildrenFromParent",
      "assignChildrenToParent"
    ]),

    changeView() {
      this.showUserList = !this.showUserList;
      if (!this.showUserList) {
        this.selectedStudents = [];
        this.$emit("updateMode", 0);
      } else {
        this.searchData();
      }
    },

    async loadAssignedStudents() {
      try {
        this.loading = true;
        this.assignedStudents = await this.getParentChildren(this.user.id);
      } catch (error) {
        this.assignedStudents = [];
        this.$toasted.error(error.message, {
          icon: "exclamation-triangle",
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
      } finally {
        this.loading = false;
      }
    },

    async loadAvailableStudents() {
      try {
        this.fetchingNextRows = true;
        const result = await userService.fetchPaginatedUsersList({
          type: UserType.STUDENT,
          pageSize: this.pageSize,
          nextPageToken: this.nextPageToken,
          searchString: this.searchString
        });

        if (this.nextPageToken) {
          this.allAvailableStudents = this.allAvailableStudents.concat(result.payload);
        } else {
          this.allAvailableStudents = result.payload;
        }

        this.nextPageToken = result.nextPageToken;
        this.isNextRecordAvailable = !!result.nextPageToken;
      } catch (error) {
        this.$toasted.error(error.message, {
          icon: "exclamation-triangle",
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
      } finally {
        this.fetchingNextRows = false;
      }
    },

    searchData() {
      this.nextPageToken = undefined;
      this.searchString = this.searchText;
      this.isSearching = true;
      this.allAvailableStudents = [];
      this.loadAvailableStudents().then(() => {
        this.isSearching = false;
      });
    },

    isStudentSelected(studentId) {
      return this.selectedStudents.some(student => student.id === studentId);
    },

    toggleStudentSelection(student) {
      const index = this.selectedStudents.findIndex(s => s.id === student.id);
      if (index === -1) {
        this.selectedStudents.push(student);
      } else {
        this.selectedStudents.splice(index, 1);
      }
    },

    async assignSelectedStudents() {
      if (this.selectedStudents.length === 0) return;

      this.assigning = true;

      try {
        const assigned = await this.assignChildrenToParent({
          studentIDs: this.selectedStudents.map(student => student.id),
          parentID: this.user.id,
        });

        if (assigned) {
          this.assignedStudents = [...this.assignedStudents, ...this.selectedStudents];
        }

        this.selectedStudents = [];
        this.changeView();
      } catch (error) {
        this.$toasted.error(error.message, {
          icon: "exclamation-triangle",
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
      }
      finally {
        this.assigning = false;
      }
    },

    async unassignStudent(studentId) {
      try {
        this.unassigningIds.push(studentId);

        const isDeleted = await this.removeChildrenFromParent({
          studentIDs: [studentId],
          parentID: this.user.id
        });

        if (isDeleted) {
          this.assignedStudents = this.assignedStudents.filter(s => s.id !== studentId);
        }
      } catch (error) {
        this.$toasted.error(error.message, {
          icon: "exclamation-triangle",
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
      }
      finally {
        this.unassigningIds = this.unassigningIds.filter(id => id !== studentId);
      }
    },

    showUnassignConfirmPopup(event, student) {
      event.preventDefault();
      event.stopPropagation();

      this.$bvModal.msgBoxConfirm(
          `Are you sure you want to unassign: ${student.firstName} ${student.lastName}?`,
          {
            title: "Confirm Unassign",
            size: "md",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: "Yes",
            cancelTitle: "No",
            footerClass: "p-2",
            centered: true
          }
      ).then(confirmed => {
        if (confirmed) {
          this.unassignStudent(student.id);
        }
      }).catch(err => {});
    },

    shortenedFullName(firstName, lastName) {
      const fullName = `${firstName} ${lastName}`;
      return fullName.length > 30 ? fullName.substring(0, 28) + '...' : fullName;
    },

    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 10 && !this.fetchingNextRows && this.isNextRecordAvailable) {
        this.loadAvailableStudents();
      }
    }
  },

  created() {
    this.loadAssignedStudents();
  }
};
</script>

<style scoped>
.student-assignment-container {
  margin-top: 20px;
}

.header-section {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
}

.back-button {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 1.2rem;
  transition: transform 0.2s;
}

.back-button:hover {
  transform: scale(1.1);
}

.main-label {
  font-weight: 500;
  font-size: 1.2rem;
  color: #333;
  margin: 0 !important;
}

.header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.assign-btn {
  background-color: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
  height: 36px;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  transition: all 0.2s;
}

.assign-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
}

.assign-btn:disabled {
  background: white;
  border-color: #666;
  cursor: not-allowed;
  color: #666;
}

.searchbar {
  position: relative;
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: none;
  border-bottom: 2px solid #ddd;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-bottom-color: var(--primary-color);
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ddd;
}

.students-list {
  max-height: 24.5rem;
  overflow-y: auto;
}

.student-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.student-item:last-child {
  border-bottom: none;
}

.student-label {
  flex: 1;
  cursor: pointer;
  margin: 0;
}

.student-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.student-details {
  font-size: 12px;
  color: #666;
}

.table-container {
  overflow-y: auto;
  max-height: 26.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  vertical-align: middle;
}

.table th {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  z-index: 2;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
}

.table tr:hover {
  background-color: #f8f9fa;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-center {
  text-align: center;
}

.unassign-btn {
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid #dc3545;
  background-color: transparent;
  border-radius: 4px;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s;
}

.unassign-btn:hover {
  background-color: #dc3545;
  color: white;
}

.loading-section {
  text-align: center;
  margin: 9.5rem 0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.students-list .loading-section {
  margin: 0;
  padding: 2rem;
}

.spinner {
  display: inline;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.assign-btn:disabled .spinner,
.unassign-btn:disabled .spinner {
  border-top-color: #ddd !important;
}


@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-content {
  text-align: center;
  margin: 7.5rem 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-state i {
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.empty-state h5 {
  margin: 0;
  color: #333;
}

.user-thumbnail {
  width: 50px; /* Example width */
  height: 50px; /* Must be equal to width for a perfect circle */
  border-radius: 50%;
  object-fit: cover; /* Ensures the image fills the circular container */
  overflow: hidden; /* Hides any parts of the image extending beyond the circle */
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .table-container {
    font-size: 12px;
  }
}
</style>